import React, { useMemo } from "react";
import { Card, Button, ButtonGroup, Row, Col } from "react-bootstrap";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux";
import { openModal } from "../../../store/features/ui/uiActions";
import {
  changeView,
  navigateToDate,
} from "../../../store/features/calendar/calendarActions";
import { MODAL_TYPES } from "../../../store/features/ui/uiTypes";
import { setSelectedAppointment } from "../../../store/features/appointments/appointmentsActions";
import { CALENDAR_VIEWS } from "../../../store/features/calendar/calendarTypes";

const localizer = momentLocalizer(moment);

const CalendarView = () => {
  const dispatch = useAppDispatch();
  const { view, date, selectedAgendas } = useAppSelector(
    (state) => state.calendar
  );
  const { items: appointments, loading } = useAppSelector(
    (state) => state.appointments
  );
  const { items: agendas } = useAppSelector((state) => state.agendas);
  const { role } = useAppSelector((state) => state.auth);

  const filteredAppointments = useMemo(() => {
    if (selectedAgendas.length === 0) return appointments;
    return appointments.filter((apt) => selectedAgendas.includes(apt.agenda));
  }, [appointments, selectedAgendas]);

  const calendarEvents = useMemo(() => {
    return filteredAppointments.map((apt) => ({
      id: apt.id,
      title: apt.title,
      start: new Date(apt.start),
      end: new Date(apt.end),
      resource: apt,
    }));
  }, [filteredAppointments]);

  const handleEventClick = (event) => {
    dispatch(setSelectedAppointment(event.resource));
    dispatch(openModal(MODAL_TYPES.APPOINTMENT_DETAILS));
  };

  const handleViewChange = (newView) => {
    dispatch(changeView(newView));
  };

  const handleNavigate = (newDate) => {
    dispatch(navigateToDate(newDate));
  };

  const handleBookAppointment = () => {
    dispatch(openModal(MODAL_TYPES.BOOK_APPOINTMENT));
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    const appointment = event.resource;
    const agenda = agendas.find((a) => a.id === appointment.agenda);

    let backgroundColor = "#0d6efd";
    if (agenda) {
      backgroundColor = agenda.color;
    }

    // Adjust opacity based on status
    if (appointment.status === "canceled") {
      backgroundColor = "#dc3545";
    } else if (appointment.status === "pending") {
      backgroundColor = "#ffc107";
    }

    return {
      style: {
        backgroundColor,
        borderColor: backgroundColor,
        color: appointment.status === "pending" ? "#000" : "#fff",
        borderRadius: "4px",
        opacity: appointment.status === "canceled" ? 0.7 : 1,
      },
    };
  };

  const CustomToolbar = ({ label, onNavigate, onView }) => {
    return (
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onNavigate("PREV")}
              className="me-2"
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onNavigate("NEXT")}
              className="me-3"
            >
              <ChevronRight size={16} />
            </Button>
            <h5 className="mb-0 fw-bold">{label}</h5>
          </div>
        </Col>
        <Col md={8} className="text-md-end ">
          <ButtonGroup size="sm" className="me-2 my-2 my-md-0">
            <Button
              variant={
                view === CALENDAR_VIEWS.MONTH ? "primary" : "outline-primary"
              }
              onClick={() => onView(CALENDAR_VIEWS.MONTH)}
            >
              Month
            </Button>
            <Button
              variant={
                view === CALENDAR_VIEWS.WEEK ? "primary" : "outline-primary"
              }
              onClick={() => onView(CALENDAR_VIEWS.WEEK)}
            >
              Week
            </Button>
            <Button
              variant={
                view === CALENDAR_VIEWS.DAY ? "primary" : "outline-primary"
              }
              onClick={() => onView(CALENDAR_VIEWS.DAY)}
            >
              Day
            </Button>
            <Button
              variant={
                view === CALENDAR_VIEWS.AGENDA ? "primary" : "outline-primary"
              }
              onClick={() => onView(CALENDAR_VIEWS.AGENDA)}
            >
              Agenda
            </Button>
          </ButtonGroup>
          <Button
            variant="success"
            size="sm"
            onClick={handleBookAppointment}
            className="btn-custom"
          >
            <Plus size={16} className="me-1" />
            Book Appointment
          </Button>
        </Col>
      </Row>
    );
  };

  if (loading) {
    return (
      <Card className="calendar-container">
        <Card.Body className="text-center py-5">
          <div className="loading-spinner me-2"></div>
          <span>Loading calendar...</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="calendar-container">
      <Card.Body>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          view={view}
          date={date}
          onView={handleViewChange}
          onNavigate={handleNavigate}
          onSelectEvent={handleEventClick}
          eventPropGetter={eventStyleGetter}
          components={{
            toolbar: CustomToolbar,
          }}
          formats={{
            timeGutterFormat: "HH:mm",
            eventTimeRangeFormat: ({ start, end }) => {
              return `${moment(start).format("HH:mm")} - ${moment(end).format(
                "HH:mm"
              )}`;
            },
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default CalendarView;
